import { SidebarMenuItem } from "oc/configs/constants";

/**
 * Deep clones a sidebar menu item with optional property overrides.
 * This prevents mutation of the original menu configuration objects.
 *
 * @param item - The menu item to clone
 * @param overrides - Optional properties to override in the cloned item
 * @returns A new menu item with cloned children (if any)
 */
export const cloneMenuItem = <T extends SidebarMenuItem>(
  item: T,
  overrides?: Partial<T>,
): T => {
  const cloned = { ...item };

  // Deep clone children array if present
  if ("children" in item && Array.isArray(item.children)) {
    (cloned as any).children = item.children.map((child) =>
      cloneMenuItem(child as SidebarMenuItem),
    );
  }

  // Apply overrides after cloning
  if (overrides) {
    Object.assign(cloned, overrides);
  }

  return cloned;
};

/**
 * Deep clones a sidebar menu item and updates the path of a specific child.
 * Useful for menu items with collapse children where child paths need dynamic updates.
 *
 * @param item - The menu item to clone
 * @param childIndex - Index of the child to update
 * @param childPath - New path for the child
 * @returns A new menu item with the updated child path
 */
export const cloneMenuItemWithChildPath = <T extends SidebarMenuItem>(
  item: T,
  childIndex: number,
  childPath: string,
): T => {
  const cloned = cloneMenuItem(item);

  if ("children" in cloned && Array.isArray(cloned.children)) {
    const child = cloned.children[childIndex];
    if (child && "path" in child) {
      (child as any).path = childPath;
    }
  }

  return cloned;
};
